import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { ImportHistoryRow, ImportIssueGroup, ImportPreviewSummary } from '../api/types';
import { EmptyRow, ErrorBanner, formatDateTime } from '../components/ui';

type ImportType = 'hr' | 'employees' | 'schedules';

// The 'hr' key is the wire and storage value — dbo.ImportBatches.ImportType
// carries it and CK_Batch_Type constrains it, so it stays put. Only the label
// changes: "Punch Data" says what is in the file, matching the other two tabs,
// where "HR Import" named where it came from.
const TABS: { key: ImportType; label: string; dropLabel: string }[] = [
  { key: 'hr', label: 'Punch Data', dropLabel: 'punch data' },
  { key: 'employees', label: 'Employees', dropLabel: 'Employees' },
  { key: 'schedules', label: 'Schedules', dropLabel: 'Schedules' },
];

/**
 * The one screen where the two-database split has a visible consequence: all
 * three types land in the HR employee database, but validation reaches into
 * both — an unknown Employee ID can only be answered by the HR master, while
 * an unrecognised location is checked against the Facilities master in the
 * facility database.
 *
 * Nothing is written until Commit.
 */
export function DataImport() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<ImportType>('hr');
  const [preview, setPreview] = useState<ImportPreviewSummary | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [committed, setCommitted] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const fields = useQuery({
    queryKey: ['import-fields', tab],
    queryFn: () => api.get<{ required: string; optional: string }>('/api/imports/fields', { type: tab }),
  });

  const history = useQuery({
    queryKey: ['import-history'],
    queryFn: () => api.get<ImportHistoryRow[]>('/api/imports/history'),
  });

  const upload = useMutation({
    mutationFn: (file: File) => api.upload<ImportPreviewSummary>(`/api/imports/preview?type=${tab}`, file),
    onSuccess: (s) => { setPreview(s); setError(null); },
    onError: (e) => setError(e.message),
  });

  const commit = useMutation({
    mutationFn: (batchId: number) =>
      api.post<{
        import: { applied: number; summary: string };
        dailyRecords: { inserted: number; updated: number; retired: number };
      }>(`/api/imports/${batchId}/commit`),
    onSuccess: (r) => {
      setPreview(null);
      // Retirements are reported rather than folded into "refreshed": rows
      // disappearing from the queue is the one outcome someone would otherwise
      // notice only by missing them.
      const { inserted, updated, retired } = r.dailyRecords;
      setCommitted(
        `${r.import.summary}. Daily records regenerated: ${inserted} new, ${updated} refreshed` +
          (retired > 0 ? `, ${retired} retired (no longer a scheduled or punched day).` : '.'),
      );
      queryClient.invalidateQueries();
      setTimeout(() => setCommitted(null), 8000);
    },
    onError: (e) => setError(e.message),
  });

  const discard = useMutation({
    mutationFn: (batchId: number) => api.post(`/api/imports/${batchId}/discard`),
    onSuccess: () => { setPreview(null); queryClient.invalidateQueries({ queryKey: ['import-history'] }); },
  });

  // The template comes from the API rather than being built here, so the
  // headers can never drift from the aliases the parser actually accepts.
  const templateDownload = useMutation({
    mutationFn: () => api.download('/api/imports/template', { type: tab }),
    onError: (e) => setError(e.message),
  });

  const take = (files: FileList | null) => {
    const file = files?.[0];
    if (file) upload.mutate(file);
  };

  const active = TABS.find((t) => t.key === tab)!;

  const busy = upload.isPending
    ? {
        title: 'Reading your file…',
        detail: 'Parsing rows and checking each one against the employee master and the Facilities list. '
          + 'Nothing is written yet — you will see a preview first.',
      }
    : commit.isPending
      ? {
          title: 'Committing the import…',
          detail: 'Writing the records, then regenerating daily records so scores reflect the new data. '
            + 'A large punch file can take a moment. Do not close this tab.',
        }
      : null;

  return (
    <div>
      {busy && (
        <div className="busy-overlay" role="status" aria-live="polite">
          <div className="busy-card">
            <div className="spinner" />
            <div className="busy-card__title">{busy.title}</div>
            <div className="busy-card__detail">{busy.detail}</div>
          </div>
        </div>
      )}

      <div className="page-header">
        <div>
          <div className="page-title">Data Import</div>
          <div className="page-subtitle">Upload punch data, employee records, or weekly schedules</div>
        </div>
      </div>

      <div className="segmented" style={{ marginBottom: 20 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`segmented__item${tab === t.key ? ' segmented__item--active' : ''}`}
            onClick={() => { setTab(t.key); setPreview(null); setError(null); }}
            style={{ padding: '8px 16px', fontSize: 13 }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{
        background: 'var(--surface-muted)', border: '1px solid var(--divider)', borderRadius: 8,
        padding: '12px 16px', marginBottom: 14, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <strong style={{ color: 'var(--text)' }}>Required:</strong> {fields.data?.required ?? '…'}
            <br />
            <strong style={{ color: 'var(--text)' }}>Optional:</strong> {fields.data?.optional ?? '…'}
          </div>
          <button
            className="btn btn--sm"
            style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            disabled={templateDownload.isPending}
            onClick={() => templateDownload.mutate()}
          >
            {templateDownload.isPending ? 'Preparing…' : `↓ ${active.label} template`}
          </button>
        </div>
        <div style={{ marginTop: 8, color: 'var(--muted)' }}>
          The template carries the exact column headers this tab expects, plus
          example rows showing the date and time formats.{' '}
          <strong style={{ color: 'var(--text)' }}>Delete the example rows before uploading</strong> —
          they use placeholder Employee IDs in the 900000 range.
        </div>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {committed && <div className="banner banner--info">{committed}</div>}

      {!preview && (
        <div
          className={`dropzone${dragOver ? ' dropzone--over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); take(e.dataTransfer.files); }}
          onClick={() => fileInput.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && fileInput.current?.click()}
        >
          <input
            ref={fileInput}
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            onChange={(e) => take(e.target.files)}
          />
          <div style={{
            width: 44, height: 44, borderRadius: 10, background: 'var(--blue-tint)', margin: '0 auto 14px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{
              width: 16, height: 16, borderLeft: '2px solid var(--blue)', borderBottom: '2px solid var(--blue)',
              transform: 'rotate(-45deg)', marginTop: -4,
            }} />
          </div>
          <div style={{ fontSize: 14.5, fontWeight: 700 }}>
            {upload.isPending ? 'Reading the file…' : `Drag and drop your ${active.dropLabel} file here (CSV or Excel .xlsx)`}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4 }}>
            or <span style={{ color: 'var(--blue)', fontWeight: 600, textDecoration: 'underline' }}>browse files</span>
          </div>
        </div>
      )}

      {preview && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700 }}>
              {preview.fileName} — {preview.totalRows} rows detected
            </div>
            <button className="btn btn--sm" onClick={() => discard.mutate(preview.importBatchId)}>Cancel</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 18 }}>
            <Tile value={preview.newCount} label="new records" color="var(--green)" bg="var(--green-tint)" />
            <Tile
              value={preview.updatedCount}
              // A punch row for a day already loaded replaces that day's punches.
              label={tab === 'hr' ? 'replace days already loaded' : 'updated records'}
              color="var(--blue)"
              bg="var(--blue-tint)"
            />
            <Tile value={preview.unmatchedCount} label="held for review" color="var(--orange)" bg="var(--orange-tint)" />
            <Tile value={preview.skippedCount} label="not imported" color="var(--muted)" bg="var(--surface-muted)" />
            <Tile value={preview.errorCount} label="errors" color="var(--red)" bg="var(--red-tint)" />
          </div>

          {/* Issues are grouped by cause, not dumped row by row — the two kinds
              of fix live in different places. */}
          <IssueList
            title="HELD — these import, but need attention"
            groups={preview.unmatchedGroups}
            tone="var(--orange)"
          />
          <IssueList
            title="NOT IMPORTED — outside the scorecard scope, or no employee record"
            groups={preview.skippedGroups}
            tone="var(--muted)"
          />
          <IssueList
            title="ERROR DETAIL — these rows will be skipped if you commit now"
            groups={preview.errorGroups}
            tone="var(--red)"
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn" onClick={() => discard.mutate(preview.importBatchId)}>Discard</button>
            <button className="btn btn--primary" disabled={commit.isPending}
              onClick={() => commit.mutate(preview.importBatchId)}>
              {commit.isPending ? 'Committing…' : 'Commit import'}
            </button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 26 }}>
        <div className="section-label">IMPORT HISTORY</div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>DATE</th>
                <th>TYPE</th>
                <th>FILE</th>
                <th>IMPORTED BY</th>
                <th className="num">ROWS</th>
                <th>RESULT</th>
              </tr>
            </thead>
            <tbody>
              {history.isLoading && <EmptyRow colSpan={6}>Loading…</EmptyRow>}
              {!history.isLoading && (history.data?.length ?? 0) === 0 && (
                <EmptyRow colSpan={6}>
                  No imports yet. The current punch data was loaded directly from the source workbook.
                </EmptyRow>
              )}
              {(history.data ?? []).map((h) => (
                <tr key={h.importBatchId}>
                  <td>{formatDateTime(h.uploadedAtUtc)}</td>
                  <td className="strong">{TABS.find((t) => t.key === h.importType)?.label ?? h.importType}</td>
                  <td>{h.fileName}</td>
                  <td>{h.uploadedBy}</td>
                  <td className="num">{h.totalRows}</td>
                  <td>{h.resultSummary ?? h.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Tile({ value, label, color, bg }: { value: number; label: string; color: string; bg: string }) {
  return (
    <div style={{ background: bg, borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 11.5, color, fontWeight: 600 }}>{label}</div>
    </div>
  );
}

function IssueList({ title, groups, tone }: { title: string; groups: ImportIssueGroup[]; tone: string }) {
  if (groups.length === 0) return null;
  return (
    <>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--muted)', marginBottom: 8 }}>{title}</div>
      <div style={{ border: '1px solid var(--rule)', borderRadius: 8, overflow: 'hidden', marginBottom: 18 }}>
        {groups.map((g) => (
          <div key={g.reason} style={{ padding: '10px 14px', borderBottom: '1px solid var(--rule)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)' }}>
              <span>
                <strong>{g.count}</strong> {g.count === 1 ? 'row' : 'rows'} — {g.reason}
              </span>
              <span style={{ color: tone, fontWeight: 600 }}>
                rows {g.rows.slice(0, 6).map((r) => r.rowNumber).join(', ')}
                {g.rows.length > 6 ? '…' : ''}
              </span>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>{g.fixHint}</div>
          </div>
        ))}
      </div>
    </>
  );
}
